import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import Sentry from 'lib/sentry';

const isMobile = window.location.origin.includes('m.');

const initialState = {
  bestCourses: [],
  courses: [],
  pagination: {
    offset: 0,
    limit: isMobile ? 10 : 18,
    total: null,
  },
  sortCoursesBy: '-sold',
  sortCourseOptions: [
    {name: 'Terlaris', value: '-sold'},
    {name: 'Termurah', value: 'price'},
    {name: 'Termahal', value: '-price'},
    {name: 'Terbaru', value: '-date'},
    {name: 'Rating Tertinggi', value: '-rating'},
    {name: 'Nama Kupon (A-Z)', value: 'name'},
  ],
  filter: {
    minimum_price: null,
    maximum_price: null,
    category: [],
    merchants: [],
    age: null,
    course_types: [],
    keywords: '',
    cities: [],
    provinces: [],
    education_levels: [],
    rating: null,
  },
  filterConfig: {
    courseTypes: [],
    merchants: [],
    categories: [],
  },
  detailCourse: null,
  isChangePhoneNumberActive: false,
  isPrakerjaComingSoonToggle: false,
};

const getters = {
  getBestCourses: (state) => state.bestCourses,
  getCourses: (state) => state.courses,
  getDetailCourse: (state) => state.detailCourse,
  getPagination: (state) => state.pagination,
  getActiveSortCourses: (_, getters) => getters.getSortCourseOptions.find((option) => option.value === getters.getSortCoursesBy),
  getIsAllCoursesLoaded: (_, getters) => getters.getPagination.offset >= getters.getPagination.total,
  getKeywords: (state) => state.filter.keywords,
  getSortCoursesBy: (state) => state.sortCoursesBy,
  getCitiesFilter: (state) => state.filter.cities,
  getProvincesFilter: (state) => state.filter.provinces,
  getSortCourseOptions: (state) => state.sortCourseOptions,
  getCoursesFilter: (state) => state.filter,
  getCourseFilterConfig: (state) => state.filterConfig,
  getIsUsingFilter: (_, getters) =>
    Object.values(omit(getters.getCoursesFilter, ['keywords', 'cities', 'provinces'])).some((value) =>
      Array.isArray(value) ? value.length > 0 : !!value
    ),
  getIsUsingLocationFilter: (_, getters) => getters.getCitiesFilter.length > 0 || getters.getProvincesFilter.length > 0,
  getIsSelfPaced: (state) => state.detailCourse.course_type === 'self_paced',
  getPrakerjaComingSoonToggle: (state) => state.isPrakerjaComingSoonToggle,
};

const actions = {
  fetchFilterConfig({commit}) {
    commit('setLoading', {filterConfig: true}, {root: true});

    return this.$api
      .getNeoConfig({id: ['prakerja-filter-config']})
      .then(({data}) => {
        commit('setFilterConfig', data[0].data);
      })
      .catch((e) => {
        commit('setError', {filterConfig: true}, {root: true});
        Sentry.log('fetchFilterConfig', e);
      })
      .finally(() => {
        commit('setLoading', {filterConfig: false}, {root: true});
      });
  },
  fetchCourses({commit, getters, rootGetters}) {
    if (rootGetters.isLoadingCourses) return;
    if (getters.getPagination.offset > getters.getPagination.total) return;

    const activeCategories = rootGetters['categories/getActiveCategories'];
    let category = activeCategories;

    // make sure categories sent as array
    if (!Array.isArray(category)) {
      category = [category];
    }

    if (isEqual(category, ['all'])) category = [];
    else if (isEqual(category, ['other'])) category = [0];

    commit('setLoading', {courses: true}, {root: true});

    return this.$api
      .getCourses({
        categories: category,
        merchants: getters.getCoursesFilter.merchants,
        course_types: getters.getCoursesFilter.course_types,
        city: getters.getCitiesFilter,
        province: getters.getProvincesFilter,
        limit: getters.getPagination.limit,
        offset: getters.getPagination.offset,
        keywords: getters.getKeywords,
        sort: getters.getSortCoursesBy,
        age: getters.getCoursesFilter.age,
        rating: getters.getCoursesFilter.rating,
        price_from: getters.getCoursesFilter.minimum_price,
        price_to: getters.getCoursesFilter.maximum_price,
        education_level: getters.getCoursesFilter.education_levels,
      })
      .then((response) => {
        commit('setCourses', response.data);
        commit('setPagination', {
          offset: getters.getPagination.offset + getters.getPagination.limit,
          total: response.meta.total_count,
        });
      })
      .catch((e) => {
        commit('setError', {courses: true}, {root: true});
        Sentry.log('fetchCourses', e);
      })
      .finally(() => {
        commit('setLoading', {courses: false}, {root: true});
      });
  },
  fetchCourseDetail({commit}, id) {
    commit('setDetailCourse', null);
    commit('setLoading', {courseDetail: true}, {root: true});

    return this.$api
      .getCourseDetail({id})
      .then(({data: courseDetail}) => {
        commit('setDetailCourse', courseDetail);
        return courseDetail;
      })
      .catch((e) => {
        Sentry.log('fetchCourseDetail', e);
        return null;
      })
      .finally(() => {
        commit('setLoading', {courseDetail: false}, {root: true});
      });
  },
  async fetchBestCourses({commit, rootState}) {
    if (rootState.dataServer.bestCourses?.length > 0) {
      commit('setBestCourses', rootState.dataServer.bestCourses);
      return;
    }

    this.$api
      .getBestCourses()
      .then(({data}) => {
        commit('setBestCourses', data);
      })
      .finally(() => {
        commit('setLoading', {bestCourses: false});
      });
  },
  async fetchChangePhoneNumberToggle({commit}, platform) {
    return this.$api
      .getNeoToggles({id: ['vp-prakerja-change-phone-number'], platform})
      .then(({data}) => {
        commit('setIsChangePhoneNumberActive', data[0]?.active || false);
        return data[0]?.active || false;
      })
      .catch((e) => {
        Sentry.log('fetchChangePhoneNumberToggle', e);
        commit('setIsChangePhoneNumberActive', false);
        return false;
      });
  },
  async fetchPrakerjaComingSoonToggle({commit}, platform) {
    return this.$api
      .getNeoToggles({id: ['vp-prakerja-coming-soon'], platform})
      .then(({data}) => {
        commit('setIsPrakerjaComingSoonToggle', data[0]?.active || false);
        return data[0]?.active || false;
      })
      .catch((e) => {
        Sentry.log('fetchPrakerjaComingSoonToggle', e);
        commit('setIsPrakerjaComingSoonToggle', false);
        return false;
      });
  },
};

const mutations = {
  setCourses(state, courses) {
    state.courses = [...state.courses, ...courses];
  },
  setBestCourses(state, bestCourses) {
    state.bestCourses = bestCourses;
  },
  resetCoursesFilter(state) {
    state.filter = {
      minimum_price: null,
      maximum_price: null,
      category: [],
      course_types: [],
      cities: [],
      provinces: [],
      education_levels: [],
      keywords: '',
      rating: null,
      age: null,
    };
  },
  setFilterConfig(state, config) {
    state.filterConfig.merchants = config.merchants ?? [];
    state.filterConfig.courseTypes = config.course_types && config.course_types.length > 0 ? config.course_types : ['luring', 'webinar'];
    state.filterConfig.categories = config.categories ?? [];
  },
  setCoursesFilter(state, filter) {
    state.filter = {...state.filter, ...filter};
  },
  resetCourses(state) {
    state.courses = [];
    state.pagination = {...state.pagination, offset: 0};
    state.isLoading = {
      courses: false,
    };
  },
  setPagination(state, payload) {
    state.pagination = {...state.pagination, ...payload};
  },
  setSortCoursesBy(state, sortBy) {
    state.sortCoursesBy = sortBy;
  },
  setDetailCourse(state, course) {
    state.detailCourse = course;
  },
  setIsChangePhoneNumberActive(state, isActive) {
    state.isChangePhoneNumberActive = isActive;
  },

  setIsPrakerjaComingSoonToggle(state, isActive) {
    state.isPrakerjaComingSoonToggle = isActive;
  },
};

export default {
  namespaced: true,
  state: {...initialState},
  getters,
  actions,
  mutations,
};
